/**
 * MultiProjectContext - Manages data aggregation for multiple projects
 *
 * This context sits above ProjectDataContext and handles:
 * - Loading multiple projects simultaneously
 * - Aggregating nodes from selected projects
 * - Maintaining project metadata and color coding
 * - Project switching and filtering
 *
 * REFACTORED: Extracted all logic into custom hooks for maintainability
 */

import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { ProjectInfo } from '@/features/projects/services/projectService';
import { AsyncErrorBoundary } from '@/shared/components/AsyncErrorBoundary';
import { log } from '@/shared/utils/logger';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import type { ProjectRecoveryBridge } from '@/shared/api/utils/syncRecovery';

// Import extracted hooks
import { useProjectLoading } from './hooks/useProjectLoading';
import { useProjectDataLoader } from './hooks/useProjectDataLoader';
import { useProjectSelection } from './hooks/useProjectSelection';
import { useProjectCache } from './hooks/useProjectCache';
import { useProjectAggregation } from './hooks/useProjectAggregation';
import { useProjectOperations } from './hooks/useProjectOperations';
import { useProjectVisibility } from './hooks/useProjectVisibility';
import { useProjectMetadata } from './hooks/useProjectMetadata';
import { clearAssigneeDirectoryCache, useAssigneeDirectory } from './hooks/useAssigneeDirectory';
import { getNodeAssignees } from '@/shared/utils/projectDataUtils';
import {
  loadSavedProjectCatalog,
  migrateLegacyProjectStateForUser,
  loadSavedSelections,
  persistProjectCatalog,
  useProjectPersistence,
  type ProjectPersistenceStorageScope,
} from './hooks/useProjectPersistence';

// Import types
import type {
  ProjectDataWithMeta,
  MultiProjectContextType,
  MultiProjectProviderProps
} from './types/multiProject';

/* eslint-disable react-refresh/only-export-components -- backward-compatible re-exports from context module */
// Re-export utilities for backward compatibility
export {
  isNamespacedId,
  getNodeId,
  getProjectIdFromNodeId,
  getProjectColor,
  PROJECT_COLORS,
  hashCode
} from './utils/projectUtils';

// Re-export types for backward compatibility
export type {
  AggregatedNode,
  ProjectInfoWithColor,
  ProjectDataWithMeta
} from './types/multiProject';
/* eslint-enable react-refresh/only-export-components */

const MultiProjectContext = createContext<MultiProjectContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook is conventional for React context files
export const useMultiProject = () => {
  const context = useContext(MultiProjectContext);
  if (!context) {
    throw new Error('useMultiProject must be used within MultiProjectProvider');
  }
  return context;
};

export const MultiProjectProvider: React.FC<MultiProjectProviderProps> = ({ children }) => {
  const { user, isViewingAs, viewAsUserId } = useAuth();
  const userId = typeof user?.id === 'string' ? user.id : null;
  const persistenceUserId = isViewingAs && viewAsUserId ? viewAsUserId : userId;
  const storageScope: ProjectPersistenceStorageScope = isViewingAs ? 'session' : 'local';
  const storageOptions = React.useMemo(
    () => ({ userId: persistenceUserId, storageScope }),
    [persistenceUserId, storageScope]
  );
  const storageKey = `${storageScope}:${persistenceUserId || ''}`;
  const recoveryProviderId = React.useId();
  const recoveryIdentity = JSON.stringify([userId, storageKey, getAuthRuntimeSessionIdentity()]);
  const recoveryLifecycle = React.useRef({ identity: recoveryIdentity, generation: 0, mounted: true });
  if (recoveryLifecycle.current.identity !== recoveryIdentity) {
    recoveryLifecycle.current.identity = recoveryIdentity;
    recoveryLifecycle.current.generation += 1;
  }
  const recoveryEpoch = `${recoveryProviderId}:${recoveryLifecycle.current.generation}`;
  const recoveryLoads = React.useRef(new Map<string, Promise<void>>());
  const previousStorageKeyRef = React.useRef<string | null>(null);
  const [rehydratedStorageKey, setRehydratedStorageKey] = useState<string | null>(null);
  // Capture readiness during render. On an account/view-as transition the ref
  // still identifies the prior owner for this render, so persistence remains
  // disabled until the transition effect has installed the new owner's state.
  const isOwnerStorageReady = Boolean(persistenceUserId)
    && previousStorageKeyRef.current === storageKey
    && rehydratedStorageKey === storageKey;
  // Only authenticated context may choose the initial owner; a persisted
  // view-as marker alone is not authority to hydrate another user's catalog.
  const initialPersistenceOptions = React.useRef(storageOptions).current;

  // State - Use lazy initialization to prevent re-running on every render
  const [availableProjects, setAvailableProjects] = useState<ProjectInfo[]>(() =>
    initialPersistenceOptions.userId ? loadSavedProjectCatalog(initialPersistenceOptions) : []
  );
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(() =>
    initialPersistenceOptions.userId ? loadSavedSelections(initialPersistenceOptions).selections : []
  );
  const [visibleProjectIds, setVisibleProjectIds] = useState<Set<string>>(() => {
    const savedState = initialPersistenceOptions.userId ? loadSavedSelections(initialPersistenceOptions) : { selections: [] };
    const cleanVisibleIds = savedState.selections.filter(id => {
      if (typeof id === 'string' && id.includes(',')) {
        log.error('MultiProject', 'Skipping invalid project ID with comma:', id);
        return false;
      }
      return true;
    });
    return new Set(cleanVisibleIds);
  });
  const [loadingProjectIds, setLoadingProjectIds] = useState<Set<string>>(new Set());
  const [failedProjectIds, setFailedProjectIds] = useState<Set<string>>(new Set());
  const [projectsData, setProjectsData] = useState<Map<string, ProjectDataWithMeta>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [userHasPreferences, setUserHasPreferences] = useState(() => {
    const savedState = initialPersistenceOptions.userId
      ? loadSavedSelections(initialPersistenceOptions)
      : { hasPreferences: false };
    return savedState.hasPreferences;
  });
  const [requestedActiveProjectId, setActiveProjectId] = useState<string | null>(() => {
    const savedState = initialPersistenceOptions.userId
      ? loadSavedSelections(initialPersistenceOptions)
      : { activeProjectId: null, selections: [] };
    return savedState.activeProjectId || savedState.selections[0] || null;
  });
  // Catalog reconciliation can change the selection without going through
  // selectProjects. Never expose an accessible but unselected project as the
  // active write/launch context, even for the render before effects settle.
  const activeProjectId = useMemo(() => {
    const availableIds = new Set(availableProjects.map(project => project.projectId));
    const selectedAvailableIds = selectedProjectIds.filter(id => availableIds.has(id));
    return requestedActiveProjectId && selectedAvailableIds.includes(requestedActiveProjectId)
      ? requestedActiveProjectId
      : selectedAvailableIds[0] ?? null;
  }, [availableProjects, requestedActiveProjectId, selectedProjectIds]);

  // Use extracted hooks
  const { loadProjectData } = useProjectDataLoader({
    projectsData,
    loadingProjectIds,
    availableProjects,
    setProjectsData,
    setLoadingProjectIds,
    setFailedProjectIds,
  });

  const { loadProjects } = useProjectLoading({
    user,
    persistenceUserId,
    storageScope,
    hasInitialized,
    userHasPreferences,
    selectedProjectIds,
    setAvailableProjects,
    setSelectedProjectIds,
    setVisibleProjectIds,
    setHasInitialized,
    setIsLoading,
    setError
  });

  const { selectProjects } = useProjectSelection({
    projectsData,
    visibleProjectIds,
    selectedProjectIds,
    selectionScopeKey: `${storageScope}:${persistenceUserId || ''}`,
    setSelectedProjectIds,
    setVisibleProjectIds,
    setActiveProjectId,
    setError,
    loadProjectData
  });

  const { isCached, isVisible, updateProjectInCache } = useProjectCache({
    projectsData,
    visibleProjectIds,
    setProjectsData,
    setAvailableProjects
  });

  const { aggregatedNodes, visibleProjects, aggregatedData } = useProjectAggregation({
    visibleProjectIds,
    projectsData,
    activeProjectId
  });

  const { refreshProject, createProject } = useProjectOperations({
    projectsData,
    setProjectsData,
    setAvailableProjects,
    loadProjectData,
    selectProjects
  });

  const { toggleProjectVisibility, showProject, hideProject } = useProjectVisibility({
    projectsData,
    setVisibleProjectIds,
    setActiveProjectId,
    selectProjects,
    loadProjectData
  });

  const { getProjectColor, getProjectInfo } = useProjectMetadata({
    projectsData,
    availableProjects
  });

  // Persist the active owner's catalog and selection state only after rehydration.
  useProjectPersistence({
    userId: persistenceUserId,
    storageScope,
    availableProjects,
    selectedProjectIds,
    activeProjectId,
    hasInitialized,
    isOwnerStorageReady,
  });

  useEffect(() => {
    const nextStorageKey = storageKey;
    if (!persistenceUserId) {
      previousStorageKeyRef.current = nextStorageKey;
      setRehydratedStorageKey(null);
      setAvailableProjects([]);
      setSelectedProjectIds([]);
      setVisibleProjectIds(new Set());
      setActiveProjectId(null);
      setProjectsData(new Map());
      setLoadingProjectIds(new Set());
      setHasInitialized(false);
      setUserHasPreferences(false);
      return;
    }
    if (previousStorageKeyRef.current === nextStorageKey) {
      return;
    }
    previousStorageKeyRef.current = nextStorageKey;

    migrateLegacyProjectStateForUser(storageOptions);

    const savedState = loadSavedSelections(storageOptions);
    setAvailableProjects(loadSavedProjectCatalog(storageOptions));
    setSelectedProjectIds(savedState.selections);
    setVisibleProjectIds(new Set(savedState.selections));
    setActiveProjectId(savedState.activeProjectId || savedState.selections[0] || null);
    setProjectsData(new Map());
    setLoadingProjectIds(new Set());
    setHasInitialized(false);
    setUserHasPreferences(savedState.hasPreferences);
    clearAssigneeDirectoryCache([]);
    setRehydratedStorageKey(nextStorageKey);
  }, [persistenceUserId, storageKey, storageOptions]);

  // Load projects on mount
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user, loadProjects]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user || !isOwnerStorageReady) return;

    type ProjectAccessEventDetail = {
      action?: string;
      projectId?: string;
      reason?: string;
      type?: string;
      userId?: string;
      user_id?: string;
    };

    const handleProjectAccessUpdated = (event: Event) => {
      const detail =
        event instanceof CustomEvent
          ? (event.detail as ProjectAccessEventDetail | undefined)
          : undefined;

      const affectedUserId = detail?.userId || detail?.user_id;
      // A delayed event for the previous account must not change this owner's
      // catalog. Membership removal requires an explicit matching recipient.
      if (affectedUserId && affectedUserId !== persistenceUserId) return;
      if (detail?.action === 'removed' && affectedUserId !== persistenceUserId) return;

      const deletedProjectId =
        detail?.projectId &&
        (detail.action === 'removed' ||
          detail.action === 'project-deleted' ||
          detail.action === 'project-archived' ||
          detail.reason === 'project-deleted')
          ? detail.projectId
          : null;

      if (deletedProjectId) {

        setAvailableProjects((current) => {
          if (!current.some((project) => project.projectId === deletedProjectId)) {
            return current;
          }

          const next = current.filter((project) => project.projectId !== deletedProjectId);
          persistProjectCatalog(next, storageOptions);
          return next;
        });

        setProjectsData((current) => {
          if (!current.has(deletedProjectId)) {
            return current;
          }

          const next = new Map(current);
          next.delete(deletedProjectId);
          return next;
        });

        setSelectedProjectIds((current) => {
          if (!current.includes(deletedProjectId)) {
            return current;
          }

          const next = current.filter((projectId) => projectId !== deletedProjectId);
          setActiveProjectId((activeProjectId) =>
            activeProjectId === deletedProjectId ? next[0] ?? null : activeProjectId
          );
          return next;
        });

        setVisibleProjectIds((current) => {
          if (!current.has(deletedProjectId)) {
            return current;
          }

          const next = new Set(current);
          next.delete(deletedProjectId);
          return next;
        });

        setLoadingProjectIds((current) => {
          if (!current.has(deletedProjectId)) return current;
          const next = new Set(current);
          next.delete(deletedProjectId);
          return next;
        });
      }

      clearAssigneeDirectoryCache(Array.from(visibleProjectIds));
      void loadProjects();
    };

    const handleAuthUpdated = (event: Event) => {
      const detail =
        event instanceof CustomEvent
          ? (event.detail as ProjectAccessEventDetail | undefined)
          : undefined;

      if (detail?.type === 'project_access_updated') {
        handleProjectAccessUpdated(event);
        return;
      }

      log.debug('MultiProject', 'Ignoring auth-updated event without project access mutation payload');
    };

    window.addEventListener('project_access_updated', handleProjectAccessUpdated);
    window.addEventListener('auth-updated', handleAuthUpdated);

    return () => {
      window.removeEventListener('project_access_updated', handleProjectAccessUpdated);
      window.removeEventListener('auth-updated', handleAuthUpdated);
    };
  }, [user, loadProjects, visibleProjectIds, storageOptions, persistenceUserId, isOwnerStorageReady]);

  // Load project data when projects become visible
  useEffect(() => {
    const visibleIds = Array.from(visibleProjectIds).filter(id => id && id !== 'undefined');

    if (user && visibleIds.length > 0 && availableProjects.length > 0) {
      // Only load data for visible projects that aren't cached
      visibleIds.forEach(projectId => {
        if (!projectId) {
          log.warn('MultiProject', 'Skipping undefined project ID in visibleIds');
          return;
        }

        const projectExists = availableProjects.some(p => p.projectId === projectId);
        const cachedProject = projectsData.get(projectId);
        const isLoaded = cachedProject?.isLoaded;

        // A project whose load failed waits for an explicit retry; refetching it on every
        // cache change would just fail again silently.
        if (projectExists && !isLoaded && !failedProjectIds.has(projectId)) {
          loadProjectData(projectId).catch(() => {
            // Failed to load project
          });
        }
      });
    }
  }, [user, visibleProjectIds, loadProjectData, projectsData, availableProjects, failedProjectIds]);

  useEffect(() => {
    if (requestedActiveProjectId !== activeProjectId) {
      setActiveProjectId(activeProjectId);
    }
  }, [activeProjectId, requestedActiveProjectId]);

  useEffect(() => {
    const allowedProjectIds = new Set(availableProjects.map(project => project.projectId));
    setProjectsData(current => {
      let changed = false;
      const next = new Map<string, ProjectDataWithMeta>();
      current.forEach((value, key) => {
        if (allowedProjectIds.has(key)) {
          next.set(key, value);
          return;
        }
        changed = true;
      });
      return changed ? next : current;
    });
  }, [availableProjects]);

  // Memoize arrays to prevent unnecessary re-renders
  const visibleProjectIdsArray = useMemo(() => Array.from(visibleProjectIds), [visibleProjectIds]);
  const cachedProjectIdsArray = useMemo(() => Array.from(projectsData.keys()), [projectsData]);
  const {
    assigneeDirectory,
    getProjectMemberIds,
    getTaggableUserIds,
    getTaggableUsers,
    getProjectMembers,
    getProjectAgents,
    getAgentProfile,
    getAssigneeDisplayName,
  } = useAssigneeDirectory({
    projectIds: visibleProjectIdsArray,
    userId: user?.id || null,
  });

  useEffect(() => {
    if (assigneeDirectory.size === 0) return;

    setProjectsData((current) => {
      let changed = false;
      const next = new Map(current);

      current.forEach((project, projectId) => {
        let projectChanged = false;
        const nextNodes = project.nodes.map((node) => {
          const rawAssignees = getNodeAssignees(node);
          if (rawAssignees.length === 0) {
            return node;
          }

          const assigneeDisplayNames = rawAssignees.map((assignee) => getAssigneeDisplayName(assignee));
          const currentDisplayNames = Array.isArray(node.assigneeDisplayNames) ? node.assigneeDisplayNames : [];
          const unchanged =
            currentDisplayNames.length === assigneeDisplayNames.length &&
            currentDisplayNames.every((value, index) => value === assigneeDisplayNames[index]);

          if (unchanged) {
            return node;
          }

          projectChanged = true;
          return {
            ...node,
            assigneeDisplayNames,
          };
        });

        if (projectChanged) {
          changed = true;
          next.set(projectId, {
            ...project,
            nodes: nextNodes,
          });
        }
      });

      return changed ? next : current;
    });
  }, [assigneeDirectory, setProjectsData, getAssigneeDisplayName]);

  // Memoize context value to prevent re-renders when reference doesn't change
  const value: MultiProjectContextType = useMemo(() => ({
    // Project management
    availableProjects,
    selectedProjectIds,
    visibleProjectIds: visibleProjectIdsArray,
    loadingProjectIds,
    projectsData,

    // Separate project data (NEW - use this instead of aggregatedData)
    visibleProjects,

    // Aggregated data (DEPRECATED - will be removed)
    aggregatedNodes,
    aggregatedData,

    // Actions
    loadProjects,
    selectProjects,
    loadProjectData,
    refreshProject,
    createProject,

    // New visibility controls
    toggleProjectVisibility,
    showProject,
    hideProject,

    // Cache info
    cachedProjectIds: cachedProjectIdsArray,
    isCached,
    isVisible,

    // Project metadata
    getProjectColor,
    getProjectInfo,
    assigneeDirectory,
    getProjectMemberIds,
    getTaggableUserIds,
    getTaggableUsers,
    getProjectMembers,
    getProjectAgents,
    getAgentProfile,
    getAssigneeDisplayName,

    // WebSocket sync
    updateProjectInCache,

    // State
    isLoading,
    error,
    failedProjectIds,
    activeProjectId,
    setActiveProject: (pid: string | null) => setActiveProjectId(pid)
  }), [
    availableProjects, selectedProjectIds, visibleProjectIdsArray, loadingProjectIds, projectsData,
    visibleProjects, aggregatedNodes, aggregatedData, loadProjects, selectProjects, loadProjectData,
    refreshProject, createProject, toggleProjectVisibility, showProject, hideProject,
    cachedProjectIdsArray, isCached, isVisible, getProjectColor, getProjectInfo,
    assigneeDirectory, getProjectMemberIds, getTaggableUserIds, getTaggableUsers, getProjectMembers,
    getProjectAgents, getAgentProfile, getAssigneeDisplayName,
    updateProjectInCache, isLoading, error, failedProjectIds, activeProjectId
  ]); 

  // Read the latest owner/target scope even before the publication effect runs.
  // A retained old bridge cannot become valid again after A -> B -> A.
  const recoveryScope = React.useRef({
    epoch: recoveryEpoch, ready: isOwnerStorageReady, runtimeIdentity: getAuthRuntimeSessionIdentity(),
    availableIds: availableProjects.map(project => project.projectId),
    targetIds: [...visibleProjectIdsArray, ...(activeProjectId ? [activeProjectId] : [])], refreshProject,
  });
  recoveryScope.current = {
    epoch: recoveryEpoch, ready: isOwnerStorageReady, runtimeIdentity: getAuthRuntimeSessionIdentity(),
    availableIds: availableProjects.map(project => project.projectId),
    targetIds: [...visibleProjectIdsArray, ...(activeProjectId ? [activeProjectId] : [])], refreshProject,
  };

  useEffect(() => {
    const lifecycle = recoveryLifecycle.current;
    lifecycle.mounted = true;
    const loads = recoveryLoads.current;
    return () => {
      lifecycle.mounted = false;
      loads.clear();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const windowGlobal = window as typeof window & { __multiProjectContext?: Record<string, unknown> };
    const getRecoveryOwnerEpoch = () => {
      const scope = recoveryScope.current;
      return recoveryLifecycle.current.mounted && scope.ready && scope.epoch === recoveryEpoch &&
        scope.runtimeIdentity === getAuthRuntimeSessionIdentity() ? recoveryEpoch : null;
    };
    const assertOwner = (expectedEpoch = recoveryEpoch) => {
      if (!getRecoveryOwnerEpoch() || expectedEpoch !== recoveryEpoch) {
        throw new Error('Project recovery belongs to an unavailable or obsolete owner session');
      }
    };
    const reloadProjects: ProjectRecoveryBridge['reloadProjects'] = async (projectIds, expectedEpoch) => {
      assertOwner(expectedEpoch);
      const scope = recoveryScope.current;
      const targets = [...new Set(projectIds ?? scope.targetIds)].filter(id => Boolean(id));
      if (!targets.length) throw new Error('No current projects are available for recovery');
      if (targets.some(id => !scope.availableIds.includes(id))) {
        throw new Error('Project recovery target is not available to the current owner');
      }
      const reads = targets.map(projectId => {
        const key = JSON.stringify([recoveryEpoch, projectId]);
        const existing = recoveryLoads.current.get(key);
        if (existing) return existing;
        const read = (async () => {
          assertOwner(expectedEpoch);
          await scope.refreshProject(projectId);
          assertOwner(expectedEpoch);
        })();
        recoveryLoads.current.set(key, read);
        // Attach both handlers without creating an ignored rejected promise.
        const release = () => {
          if (recoveryLoads.current.get(key) === read) recoveryLoads.current.delete(key);
        };
        void read.then(release, release);
        return read;
      });
      const results = await Promise.allSettled(reads);
      assertOwner(expectedEpoch);
      const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failure) throw failure.reason;
    };
    const published = {
      ...(windowGlobal.__multiProjectContext ?? {}),
      availableProjectIds: availableProjects.map((project) => project.projectId),
      selectedProjectIds: [...selectedProjectIds],
      visibleProjectIds: [...visibleProjectIdsArray],
      loadingProjectIds: Array.from(loadingProjectIds),
      hasInitialized, isLoading, activeProjectId,
      setActiveProject: (projectId: string | null) => setActiveProjectId(projectId),
      getRecoveryOwnerEpoch,
      reloadProjects,
      // Legacy fire-and-forget callers retain their interface, with rejection handled.
      reloadProject: (projectId: string) => {
        void reloadProjects([projectId]).catch(error => log.error('MultiProject', 'Project reload failed:', error));
      },
      reloadAllProjects: () => reloadProjects(),
    };
    windowGlobal.__multiProjectContext = published;
    return () => {
      if (windowGlobal.__multiProjectContext === published) delete windowGlobal.__multiProjectContext;
    };
  }, [availableProjects, selectedProjectIds, visibleProjectIdsArray, loadingProjectIds,
    hasInitialized, isLoading, activeProjectId, refreshProject, recoveryEpoch, isOwnerStorageReady]);

  return (
    <MultiProjectContext.Provider value={value}>
      <AsyncErrorBoundary
        onError={(error) => {
          log.error('MultiProject', 'Error in MultiProjectContext:', error);
          // Clear error state to allow recovery
          setError(error.message);
        }}
        resetKeys={[selectedProjectIds.join(','), user?.id ?? '']}
      >
        {children}
      </AsyncErrorBoundary>
    </MultiProjectContext.Provider>
  );
};
